// ============================================================================
// navItems.ts (E2/Paket A) — die EINZIGE Nav-Wahrheit der App.
// ----------------------------------------------------------------------------
// docs/30 §6: Bisher lebte die Navigation doppelt (AppHeader.desktopNav +
// BottomNav), beide mit je eigener, unvollstaendiger Aktiv-Logik. Folge: viele
// Routen (/world-tree, /spielbau, /world-element-edit, /characters/new,
// /characters/:id/edit, /soundboard, /chat/*, /character-chat/* …) markierten
// GAR KEINEN Nav-Eintrag als aktiv. Diese Datei schliesst die Luecken: eine
// `matches()`-Funktion je Eintrag, die JEDE Route genau EINEM Eintrag zuordnet.
//
// Regel (docs/30 §6, „Aktiv-Zustand, testbar"): fuer jede navigierbare Route
// liefert `activeNavId` genau eine Id. Der Vitest `navItems.test.ts` iteriert
// alle Routen aus `routes.tsx` und beweist das (nie zwei, nie null).
//
// `primary` steuert die Bar (Handy, shape="bar"): genau 5 Eintraege, in der
// Reihenfolge dieser Liste. Rail/Sidebar zeigen zusaetzlich die Sekundaer-
// Eintraege (Werkzeuge/Konto).
//
// W1-C (Programm „Zwei Einstiege", E13/E14, Karte C §1 + Empfehlung c): Die Bar
// heisst jetzt Chats · Abenteuer · Entdecken · Welt · Tafelrunde.
//   * NEU `chats` (/chats) — die Messenger-Flaeche. Sie ist der Sammelpunkt
//     ALLER Gespraeche mit Figuren: /chats, /character-chat/*, /group-chat/*
//     UND — als sekundaerer Landeplatz ohne eigenen Bar-Eintrag — /characters/*.
//     Deshalb gibt es kein eigenes `characters`-Item mehr; die Charakterliste
//     ist eine Unterflaeche der Chats (W4-B loest /chats vollstaendig ab).
//   * NEU `discover` (/discover) — der oeffentliche Katalog (W4-C).
//   * `tindra` faellt auf `primary: false` in die Sektion `tools` (Fuenfer-Regel
//     der Bar; der Helpbot ist ueber Rail/Sidebar und „Mehr"
//     erreichbar). Sein Kuerzel entfaellt mit dem Bar-Platz — ⌘1…5 gehoeren
//     laut AppShell genau den fuenf primaeren Eintraegen.
//
// R3/E: Die Werkzeuge (Tindra, Meilensteine, Erinnerungsbuch, Wochenrueckblick,
// Klangbuehne, Spielbau) stehen in „Mehr" (Neukonten), in Rail/Sidebar und —
// fuer Bestandskonten auf dem Handy — hinter dem „Mehr"-Zeiger der Einstellungen.
// Die klassische Bar der Bestandskonten bleibt unveraendert (E-07).
// ============================================================================

export type NavId =
  | 'chats'
  | 'adventures'
  | 'discover'
  | 'world'
  | 'table-rounds'
  | 'more'
  | 'tindra'
  | 'soundboard'
  | 'milestones'
  | 'memory-book'
  | 'week-review'
  | 'spielbau'
  | 'coins'
  | 'settings'
  | 'admin';

export type NavSection = 'play' | 'build' | 'tools' | 'account';

export interface NavItem {
  id: NavId;
  to: string;
  labelKey: string;
  icon: string;
  section: NavSection;
  /** Erscheint in der Bar (shape="bar", max. 5). */
  primary: boolean;
  /** Nur fuer Admins sichtbar. */
  adminOnly?: boolean;
  /** Reserviert fuer E4b (⌘1…5) — in E2 nur Datenfeld, kein Listener. */
  shortcut?: string;
  /** Eintrag existiert nur in der fokussierten Leiste fuer Neukonten. */
  focusOnly?: boolean;
  /**
   * Nur in der Handy-Bar sinnvoll. Rail/Sidebar zeigen ohnehin alle
   * Bereiche — ein „Mehr" dort waere eine Liste derselben Eintraege.
   */
  barOnly?: boolean;
  /** Die EINE Aktiv-Logik fuer alle drei Gestalten. */
  matches(pathname: string): boolean;
}

/** true, wenn `pathname` exakt `base` ist oder mit `base + '/'` beginnt. */
function under(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}

// Reihenfolge = DOM-Reihenfolge = Bar-Reihenfolge. Die ersten fuenf (primary)
// sind die klassische Bar: Chats · Abenteuer · Entdecken · Welt · Tafelrunde.
export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'chats',
    to: '/chats',
    labelKey: 'nav.chats',
    icon: 'chat',
    section: 'play',
    primary: true,
    shortcut: 'mod+1',
    // Alle Gespraechsflaechen mit Figuren + die Charakterverwaltung als
    // sekundaerer Landeplatz (kein eigener Bar-Eintrag mehr). /group-chat
    // gehoerte bis W1-C zu `table-rounds` — Gruppenchats sind aber Chats mit
    // mehreren Figuren, keine Tafelrunden-Sitzungen.
    matches: (p) =>
      under(p, '/chats') ||
      under(p, '/character-chat') ||
      under(p, '/group-chat') ||
      under(p, '/characters') ||
      // W2-B/F-01: die Begleiter-Tuer des Sofortstarts.
      p === '/first-scene/companion',
  },
  {
    id: 'adventures',
    to: '/dashboard',
    labelKey: 'pwa.campaigns',
    icon: 'nav-adventure',
    section: 'play',
    primary: true,
    shortcut: 'mod+2',
    matches: (p) =>
      p === '/dashboard' ||
      under(p, '/chat') ||
      under(p, '/campaigns') ||
      p === '/campaign-new' ||
      p === '/intimate-videos' ||
      // W2-B/F-01: die Abenteuer-Tuer des Sofortstarts.
      p === '/first-scene/adventure',
  },
  {
    id: 'discover',
    to: '/discover',
    labelKey: 'nav.discover',
    icon: 'compass',
    section: 'play',
    primary: true,
    shortcut: 'mod+3',
    matches: (p) => under(p, '/discover'),
  },
  {
    id: 'world',
    to: '/world',
    labelKey: 'pwa.world',
    icon: 'nav-worlds',
    section: 'build',
    primary: true,
    shortcut: 'mod+4',
    matches: (p) =>
      (under(p, '/world') && !under(p, '/world/spielbau')) ||
      p === '/world-tree' ||
      p === '/world-element-edit',
  },
  {
    id: 'table-rounds',
    to: '/table-rounds',
    labelKey: 'pwa.table_rounds',
    icon: 'nav-gamebuild',
    section: 'play',
    primary: true,
    shortcut: 'mod+5',
    matches: (p) =>
      under(p, '/table-rounds') ||
      under(p, '/table-round-chat') ||
      under(p, '/table-round-session'),
  },
  {
    // „Mehr" — nur in der fokussierten Bar der Neukonten (E-07). Fuehrt auf die
    // Uebersicht aller Bereiche und Werkzeuge (/more), in der Welt und
    // Tafelrunde samt Erklaersatz stehen, bis sie in die Leiste aufruecken.
    // Bestandskonten behalten ihre Leiste; sie erreichen /more ueber den
    // Zeiger in den Einstellungen, Rail/Sidebar zeigen die Eintraege direkt.
    id: 'more',
    to: '/more',
    labelKey: 'nav.more',
    icon: 'grid',
    section: 'tools',
    primary: false,
    focusOnly: true,
    barOnly: true,
    matches: (p) => under(p, '/more'),
  },
  {
    id: 'tindra',
    to: '/tindra',
    labelKey: 'pwa.tindra',
    // Eigenes Sprite-Symbol statt der generischen Sprechblase — die gehoert
    // seit W1-C dem Chats-Eintrag, und zwei gleiche Symbole in der Rail waeren
    // nicht unterscheidbar.
    icon: 'tindra',
    section: 'tools',
    primary: false,
    matches: (p) => under(p, '/tindra'),
  },
  {
    id: 'soundboard',
    to: '/soundboard',
    labelKey: 'soundboard.title',
    icon: 'sound',
    section: 'tools',
    primary: false,
    matches: (p) => under(p, '/soundboard'),
  },
  {
    id: 'milestones',
    to: '/milestones',
    labelKey: 'nav.milestones',
    icon: 'star',
    section: 'tools',
    primary: false,
    matches: (p) => under(p, '/milestones'),
  },
  {
    id: 'memory-book',
    to: '/erinnerungen?scope=user',
    labelKey: 'memories_book.title',
    icon: 'book',
    section: 'tools',
    primary: false,
    matches: (p) => under(p, '/erinnerungen'),
  },
  {
    id: 'week-review',
    to: '/woche',
    labelKey: 'week.nav.card_label',
    icon: 'type-calendar',
    section: 'tools',
    primary: false,
    matches: (p) => under(p, '/woche'),
  },
  {
    id: 'spielbau',
    to: '/world/spielbau',
    labelKey: 'nav.group_gamebuild',
    icon: 'dice',
    section: 'tools',
    primary: false,
    matches: (p) => under(p, '/world/spielbau') || under(p, '/spielbau'),
  },
  {
    id: 'coins',
    to: '/coins',
    labelKey: 'nav.coins',
    icon: 'coin',
    section: 'account',
    primary: false,
    matches: (p) => under(p, '/coins'),
  },
  {
    id: 'settings',
    to: '/settings',
    labelKey: 'nav.settings',
    icon: 'settings',
    section: 'account',
    primary: false,
    matches: (p) => under(p, '/settings') || p === '/profile',
  },
  {
    id: 'admin',
    to: '/admin',
    labelKey: 'nav.admin',
    icon: 'shield-sword',
    section: 'account',
    primary: false,
    adminOnly: true,
    matches: (p) => under(p, '/admin'),
  },
];

/**
 * Die aktive Nav-Id fuer einen Pfad — genau eine oder keine (fuer Routen ohne
 * Nav, z.B. /login). Bei mehreren Treffern gewinnt der ERSTE Eintrag; der
 * Vitest garantiert, dass dieser Fall in der Praxis nicht eintritt.
 */
export function activeNavId(pathname: string): NavId | null {
  const hit = NAV_ITEMS.find((item) => item.matches(pathname));
  return hit ? hit.id : null;
}

// ============================================================================
// W2-B/X-09 (E-07) — fokussierte Leiste fuer NEUKONTEN.
// ----------------------------------------------------------------------------
// Julius' Entscheidung E-07: Neukonten sehen Chats · Abenteuer · Entdecken ·
// Mehr. Welt und Tafelrunde erscheinen erst ab 30 eigenen Nachrichten oder mit
// der Einstellung „Ich leite eine Spielrunde" (bzw. sobald das Konto einen
// Tisch angelegt hat). Bestandskonten — alles, was vor dem Stichtag angelegt
// wurde — behalten ihre Leiste unveraendert.
//
// Der STICHTAG kommt vom Server (/me `nav_focus_since`, Env NAV_FOCUS_SINCE je
// Server; Review M5): Beta und Prod teilen die DB, die Beta testet aber frueher
// als das Prod-Release. Ein fest ins Bundle gebackenes Datum haette entweder die
// Beta untestbar gemacht oder Prod-Konten aus der Zwischenzeit spaeter Welt und
// Tafelrunde genommen. Ohne Stichtag (null/fehlend) bleibt es fuer alle bei der
// bekannten Leiste.
//
// Die Eingaben kommen aus /api/auth/me (nav_focus_since, account_created_at,
// own_message_count, leads_table_round) und aus clientPrefs
// (layout/useNavFocus.ts). Die ENTSCHEIDUNG steht nur hier.
//
// Platz fuer „Heute" (Tagesfaden, spaetere Welle): FOCUS_BAR_IDS bekommt dann
// 'today' VOR 'more'. Die Bar ist fuer fuenf Plaetze gebaut, die fokussierte
// Leiste nutzt heute vier.
// ============================================================================

/** Ab so vielen eigenen Nachrichten ruecken Welt und Tafelrunde in die Leiste. */
export const NAV_UNLOCK_OWN_MESSAGES = 30;

/** Reihenfolge der fokussierten Bar. Spaeter: 'today' vor 'more'. */
export const FOCUS_BAR_IDS: readonly NavId[] = ['chats', 'adventures', 'discover', 'more'];
/** Diese Bereiche wandern fuer Neukonten in die Werkzeuge (Rail/Sidebar/Mehr). */
export const FOCUS_DEMOTED_IDS: readonly NavId[] = ['world', 'table-rounds'];

export interface NavFocusInput {
  /**
   * /me `nav_focus_since` — Stichtag YYYY-MM-DD je Server. Konten ab diesem Tag
   * (UTC, Datumsteil von users.created_at) sind Neukonten. null = Leiste aus.
   */
  focusSince?: string | null;
  /** users.created_at aus /me (SQLite „YYYY-MM-DD HH:MM:SS" oder ISO). */
  accountCreatedAt?: string | null;
  /** users.message_count aus /me. */
  ownMessageCount?: number | null;
  /** /me: das Konto hat mindestens eine Tafelrunde angelegt. */
  leadsTableRound?: boolean;
  /** Einstellung „Ich leite eine Spielrunde" (clientPrefs). */
  leadsRoundPref?: boolean;
}

function datePart(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

/**
 * Sieht dieses Konto die fokussierte Leiste? Fehlen die Fakten (aelterer
 * Server, /me noch nicht da), gilt die bekannte Leiste — niemand soll wegen
 * eines fehlenden Feldes Bereiche verlieren.
 */
export function isNavFocused(input: NavFocusInput | null | undefined): boolean {
  if (!input) return false;
  const since = datePart(input.focusSince);
  if (!since) return false;
  const created = datePart(input.accountCreatedAt);
  if (!created) return false;
  if (created < since) return false;
  if (input.leadsRoundPref === true || input.leadsTableRound === true) return false;
  const count = Number(input.ownMessageCount);
  if (Number.isFinite(count) && count >= NAV_UNLOCK_OWN_MESSAGES) return false;
  return true;
}

const CLASSIC_ITEMS: readonly NavItem[] = NAV_ITEMS.filter((item) => !item.focusOnly);

function buildFocusedItems(): readonly NavItem[] {
  const byId = new Map(NAV_ITEMS.map((item) => [item.id, item] as const));
  const bar: NavItem[] = FOCUS_BAR_IDS.map((id, index) => {
    const item = byId.get(id);
    if (!item) throw new Error(`navItems: unbekannte Id ${id} in FOCUS_BAR_IDS`);
    return { ...item, primary: true, shortcut: `mod+${index + 1}` };
  });
  const rest = NAV_ITEMS
    .filter((item) => !FOCUS_BAR_IDS.includes(item.id))
    .map((item) => (FOCUS_DEMOTED_IDS.includes(item.id)
      ? { ...item, primary: false, section: 'tools' as NavSection, shortcut: undefined }
      : item));
  return [...bar, ...rest];
}

const FOCUSED_ITEMS: readonly NavItem[] = buildFocusedItems();

/**
 * Die Eintraege fuer genau dieses Konto — DIE Quelle fuer AppNav (Bar/Rail/
 * Sidebar) und die ⌘1…n-Kuerzel der AppShell. Reihenfolge = DOM-Reihenfolge.
 */
export function navItemsFor(focused: boolean): readonly NavItem[] {
  return focused ? FOCUSED_ITEMS : CLASSIC_ITEMS;
}

/**
 * Die Eintraege der „Mehr"-Uebersicht: alles, was NICHT in der Bar steht und
 * kein Admin-/Bar-only-Eintrag ist, in Leisten-Reihenfolge.
 */
export function moreItemsFor(focused: boolean): readonly NavItem[] {
  return navItemsFor(focused).filter((item) => !item.primary && !item.adminOnly && !item.barOnly);
}
